import { WmButton } from "@watermarkinsights/ripple-react";
import { SyntheticEvent, useEffect, useReducer, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { sendRequest } from "@/common/http";
import translate from "@/i18n/translate";
import "../Lightcast.scss";
import ApiError from "../../common/elements/ApiError";
import { handleChartArrowNavigation } from "../../common/helper";

interface PopulationByAge {
  ageFrom: string;
  ageTo: string;
  changePercentage: number;
  id: number;
  order: number;
}

interface PopulationByAgeResultset {
  currentYear: number;
  pastYear: number;
  populationByAge: PopulationByAge[];
}
interface PopulationByAgeProps {
  setTooglePosition: (event: any, chartId: string) => void;
  onTabLoad: (tab: string) => void;
  onComponentLoad: (component: string) => void;
  showIntegratedHeader: boolean;
}

interface PopulationByAgeState {
  changeInPopulationRange: number[];
  changePercentages: number[] | null;
  setBars: boolean;
  currentYear: number | null;
  pastYear: number | null;
  populationByAge: PopulationByAge[];
  apiError: boolean;
}

interface PopulationByAgeAction {
  type: string;
  changeInPopulationRange: number[];
  changePercentages: number[];
  populationByAge: PopulationByAgeResultset;
  flag: boolean;
}

const defaultPopulationByAgeResultset = {
  currentYear: 0,
  pastYear: 0,
  populationByAge: []
};

const defaultPopulationByAgeAction = {
  type: "",
  changeInPopulationRange: [],
  changePercentages: [],
  populationByAge: defaultPopulationByAgeResultset,
  flag: false
};

const reducer = (
  state: PopulationByAgeState,
  action: PopulationByAgeAction
) => {
  switch (action.type) {
    case "setChangeInPopulationRange":
      return {
        ...state,
        changeInPopulationRange: action.changeInPopulationRange
      };

    case "setPopulationChangePercentages":
      const changePercentages = action.populationByAge?.populationByAge.map(
        (population: PopulationByAge) => population.changePercentage
      );
      return {
        ...state,
        changePercentages: changePercentages
      };
    case "setBarsFlag":
      return {
        ...state,
        setBars: action.flag
      };
    case "setPopulationByAgeError":
      return {
        ...state,
        currentYear: 0,
        pastYear: 0,
        populationByAge: [],
        changePercentages: [],
        apiError: true
      };

    case "setPopulationByAge":
      return {
        ...state,
        currentYear: action.populationByAge?.currentYear,
        pastYear: action.populationByAge?.pastYear,
        populationByAge: action.populationByAge?.populationByAge,
        apiError: action.populationByAge ? false : true
      };
    default:
      return state;
  }
};

export default function PopulationByAgeResultset({
  setTooglePosition,
  onTabLoad,
  onComponentLoad,
  showIntegratedHeader
}: PopulationByAgeProps) {
  const initialState = {
    changeInPopulationRange: [],
    changePercentages: null,
    setBars: true,
    currentYear: null,
    pastYear: null,
    populationByAge: [],
    apiError: false
  };
  const [openPopoverState, setOpenPopoverState] = useState(false);

  const [selectedRowId, setSelectedRowId] = useState("");
  const [focusedBarIndex, setFocusedBarIndex] = useState<number>(-1);
  const barRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [maxTicks, setMaxTicks] = useState(Number);
  const [isMoveLeftBtnDisabled, setMoveLeftBtnDisable] = useState(false);
  const [isMoveRightBtnDisabled, setMoveRightBtnDisable] = useState(true);

  const timeframe = {
    pastYear: state.pastYear,
    currentYear: state.currentYear
  };
  const populationChangeByAge = state.populationByAge;

  useEffect(() => {
    const applyResult = (result: PopulationByAgeResultset) => {
      dispatch({
        ...defaultPopulationByAgeAction,
        type: "setPopulationByAge",
        populationByAge: result
      });
      dispatch({
        ...defaultPopulationByAgeAction,
        type: "setPopulationChangePercentages",
        populationByAge: result
      });
      onTabLoad("ageCohort");
      onComponentLoad("populationByAge");
    };

    const onError = (error: any) => {
      dispatch({
        ...defaultPopulationByAgeAction,
        type: "setPopulationByAgeError"
      });

      onTabLoad("ageCohort");
      onComponentLoad("populationByAge");
    };

    sendRequest(
      { uri: "/lightcast/population_by_age", camelize: true },
      applyResult,
      onError
    );
  }, []);
  const getChangeInPopulationRange = (changePercentages: number[]) => {
    let hasNegativeVal = changePercentages.some((val: number) => val < 0);
    let hasPositiveVal = changePercentages.some((val: number) => val >= 0);
    let absPercentageChangeValArr = absoluteValueArray(changePercentages);

    var getIntervalRangeVal = getInterval(
      Math.max(...absPercentageChangeValArr),
      2
    );
    if (hasNegativeVal && !hasPositiveVal) {
      setMaxTicks(2);
      setBarClassList("negative");
      return [
        0,
        -getIntervalRangeVal / 2,
        -getIntervalRangeVal,
        -(getIntervalRangeVal + getIntervalRangeVal / 2),
        -getIntervalRangeVal * 2
      ];
    } else if (hasPositiveVal && !hasNegativeVal) {
      setMaxTicks(2);
      setBarClassList("positive");
      return [
        getIntervalRangeVal * 2,
        getIntervalRangeVal + getIntervalRangeVal / 2,
        getIntervalRangeVal,
        getIntervalRangeVal / 2,
        0
      ];
    } else {
      setMaxTicks(2);
      return [
        getIntervalRangeVal * 2,
        getIntervalRangeVal,
        0,
        -getIntervalRangeVal,
        -getIntervalRangeVal * 2
      ];
    }
  };

  const setBars = (changePercentages: any): JSX.Element => {
    let absPercentageChangeValArr = absoluteValueArray(changePercentages);
    var ele = document.querySelectorAll(".barss");

    ele.forEach(getBarsHeightVal);
    function getBarsHeightVal(item: any, index: number) {
      var n = parseFloat(changePercentages[index]) * 1;
      const getHeight = (n: number) => {
        return (
          (parseFloat(changePercentages[index]) /
            (getInterval(Math.max(...absPercentageChangeValArr), maxTicks) *
              maxTicks)) *
          100
        );
      };
      var numbersFull = getHeight(n);
      item.style.height = Math.round(Math.abs(numbersFull)) + "%";
      if (n >= 0) {
        addClassListToElement(item, "positive");
      } else {
        addClassListToElement(item, "negative");
      }
    }

    if (ele && ele.length > 0)
      dispatch({
        ...defaultPopulationByAgeAction,
        type: "setBarsFlag",
        flag: false
      });
    return <></>;
  };

  useEffect(() => {
    state.changePercentages &&
      dispatch({
        ...defaultPopulationByAgeAction,
        type: "setChangeInPopulationRange",
        changeInPopulationRange: getChangeInPopulationRange(
          state.changePercentages
        )
      });
  }, [state.changePercentages]);

  const openPopover = (event: SyntheticEvent, population: PopulationByAge) => {
    setTooglePosition(event, "cohort-chart");
    setOpenPopoverState(true);
    setSelectedRowId(String(population.id));
  };

  function useClosePopoverClick(ref: any) {
    useEffect(() => {
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setOpenPopoverState(false);
          setFocusedBarIndex(-1);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }



  const moveLeftBtn = useIntl().formatMessage({
    id: "app.changeInPopulation.moveLeftBtn"
  });

  const moveRightBtn = useIntl().formatMessage({
    id: "app.changeInPopulation.moveRightBtn"
  });
  const chartContainerAriaLabel = useIntl().formatMessage({
    id: "app.changeInPopulation.ageCohort.chartAriaLabel"
  });
  const chartInstructions = useIntl().formatMessage({
    id: "app.changeInPopulation.ageCohort.chartInstructions"
  });
  const addBoxShadowOnBothSides = (element: HTMLElement) => {
    element && element.classList.add("-box-shadows");
  };

  const removeBoxShadowOnBothSides = (element: HTMLElement) => {
    element && element.classList.remove("-box-shadows");
  };

  const addBoxShadowOnLeft = (element: HTMLElement) => {
    element && element.classList.add("-box-shadow-left");
  };

  const removeBoxShadowOnLeft = (element: HTMLElement) => {
    element && element.classList.remove("-box-shadow-left");
  };

  const addBoxShadowOnRight = (element: HTMLElement) => {
    element && element.classList.add("-box-shadow-right");
  };

  const removeBoxShadowOnRight = (element: HTMLElement) => {
    element && element.classList.remove("-box-shadow-right");
  };
  const setMoveLeftRightBtnDisability = (ref: any, state: string) => {
    let element = ref?.current;
    if (element?.scrollLeft === 0) {
      setMoveLeftBtnDisable(true);
      removeBoxShadowOnLeft(element);
      removeBoxShadowOnBothSides(element);
    } else {
      setMoveLeftBtnDisable(false);
      addBoxShadowOnLeft(element);
      removeBoxShadowOnBothSides(element);
    }

    if (
      element?.scrollLeft !== 0 &&
      element?.scrollLeft < element?.scrollWidth - element?.offsetWidth - 1
    ) {
      removeBoxShadowOnRight(element);
      removeBoxShadowOnLeft(element);
      addBoxShadowOnBothSides(element);
    }
    switch (state) {
      case "onload":
        if (
          element?.scrollLeft >
          element?.scrollWidth - element?.offsetWidth - 1
        ) {
          return (
            setMoveRightBtnDisable(false),
            addBoxShadowOnRight(element),
            removeBoxShadowOnBothSides(element)
          );
        } else {
          return setMoveRightBtnDisable(false), setMoveLeftBtnDisable(true);
        }

      default:
        if (
          element?.scrollLeft >
          element?.scrollWidth - element?.offsetWidth - 1
        ) {
          return setMoveRightBtnDisable(true), removeBoxShadowOnRight(element);
        } else {
          return setMoveRightBtnDisable(false), addBoxShadowOnRight(element);
        }
    }
  };
  function useSetMoveLeftRightValue(ref: any) {
    useEffect(() => {
      setTimeout(() => {
        setMoveLeftRightBtnDisability(ref, "onload");
      }, 200);
    }, [ref]);
  }

  const scrollWrapper = useRef(null);
  useSetMoveLeftRightValue(scrollWrapper);
  useClosePopoverClick(scrollWrapper);

  const setChartScroll = (
    element: any,
    speed: number,
    distance: number,
    direction: string
  ) => {
    let scrollAmount = 0;

    const slideTimer = setInterval(() => {
      var step =
        direction === "left" ? -element?.offsetWidth : element?.offsetWidth;
      element.scrollLeft += step;
      scrollAmount += Math.abs(step);
      if (scrollAmount >= distance) {
        clearInterval(slideTimer);
      }
      setMoveLeftRightBtnDisability(scrollWrapper, "button-click");
    }, speed);
  };

  const getPopoverAmountData = (population: PopulationByAge) => {
    return population.changePercentage >= 0 ? "-green" : "-red";
  };

  const getNumericSignValue = (changeInPercentageVal: number) => {
    return changeInPercentageVal >= 0 ? "+" : "";
  };

  const handleChartContainerKeyDown = (event: React.KeyboardEvent) => {
    handleChartArrowNavigation(event, {
      totalItems: populationChangeByAge.length,
      currentIndex: focusedBarIndex,
      itemRefs: barRefs,
      containerRef: scrollWrapper,
      onNavigate: (newIndex) => {
        setFocusedBarIndex(newIndex);
      },
      onEscape: () => {
        setOpenPopoverState(false);
        setFocusedBarIndex(-1);
      },
      onExit: () => {
        setOpenPopoverState(false);
        setFocusedBarIndex(-1);
      }
    });
  };

  return (
    <>
      {state.changePercentages && (
        <div className="population-by-age">
          <div className="age-cohort-chart flex-row -justified">
            {!showIntegratedHeader && (
              <div className="header">
                <div className="info" id="change-population-info">
                  <h3 className="title change-population-title">
                    {translate(
                      "app.changeInPopulation.ageCohort.changePopulationByAge"
                    )}
                  </h3>
                  <div className="description -descrip html-desc">
                    {translate("app.changeInPopulation.helperText")}
                  </div>
                </div>
              </div>
            )}
            {!state.apiError && (
              <>
                {showIntegratedHeader && (
                  <div id="age-cohort-header" className="age-cohort-label">
                    {translate(
                      "app.changeInPopulation.ageCohort.changePopulationByAge"
                    )}
                  </div>
                )}
                <div className="flex-row">
                  <WmButton
                    buttonType={"icononly"}
                    tooltip={moveLeftBtn}
                    icon={"f04d"}
                    tooltipPosition="bottom-left"
                    disabled={isMoveLeftBtnDisabled}
                    onClick={() =>
                      setChartScroll(scrollWrapper.current, 25, 100, "left")
                    }
                    tabIndex={-1}
                  ></WmButton>
                  <WmButton
                    buttonType={"icononly"}
                    tooltip={moveRightBtn}
                    icon={"f054"}
                    tooltipPosition="bottom-left"
                    disabled={isMoveRightBtnDisabled}
                    onClick={() =>
                      setChartScroll(scrollWrapper.current, 25, 100, "right")
                    }
                    tabIndex={-1}
                  ></WmButton>
                </div>
              </>
            )}
            <div id="chart-instructions">{chartInstructions}</div>
          </div>
          {!state.apiError && (
            <div className="flex-row -verticallycentered">
              <div className="percentage-change-label">
                {translate("app.changeInPopulation.ageCohort.percentageChange")}
              </div>
              <div
                className="cohort-chart "
                id="cohort-chart-container"
                tabIndex={0}
                role="application"
                aria-labelledby="age-cohort-header"
                aria-describedby="chart-instructions"
                ref={scrollWrapper}
                onKeyDown={handleChartContainerKeyDown}
              >
                <div className="bar-chart" id="bar-chart">
                  <div className="row bars">
                    <div className="y-axis one">
                      {state.changeInPopulationRange.map(
                        (populChange: number) => {
                          return (
                            <div className="segment" key={populChange}>
                              <span className="label">{populChange}</span>
                            </div>
                          );
                        }
                      )}
                    </div>

                    <div className="x-axis" id="x-axis">
                      {populationChangeByAge.map(
                        (population: PopulationByAge, index: number) => {
                          var changePercentage = population.changePercentage;
                          var populationAgeRange =
                            getPopulationAgeRange(population);
                          var changeState =
                            changePercentage >= 0
                              ? translate("app.trends.increased")
                              : translate("app.trends.decreased");
                          return (
                            <div className="year wrap" key={population.id}>
                              <div className="col">
                                <div
                                  className="barss tooltip"
                                  onFocus={(event: SyntheticEvent) =>
                                    openPopover(event, population)
                                  }
                                  ref={(el) => (barRefs.current[index] = el)}
                                  tabIndex={-1}
                                >
                                  <div
                                    className={`population-tooltiptext tooltiptext-years ${openPopoverState &&
                                      String(population.id) === selectedRowId
                                      ? "-visible"
                                      : "-hidden"
                                      }`}
                                  >
                                    <div className="tooltip-text">
                                      <div
                                        className={`population-popover-amount 
                              ${getPopoverAmountData(population)}`}
                                      >
                                        {getNumericSignValue(
                                          population.changePercentage
                                        ) +
                                          population.changePercentage +
                                          "%"}
                                      </div>
                                      <div className="population-popover-text">
                                        {translate(
                                          "app.changeInPopulation.popoverLabelText",
                                          {
                                            populationAgeRange:
                                              populationAgeRange,
                                            changeState: changeState,
                                            changePercentage:
                                              Math.abs(changePercentage),
                                            pastYear: timeframe.pastYear,
                                            currentYear: timeframe.currentYear
                                          }
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <span className="label">
                                {displayAgeGroup(population)}
                              </span>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {state.setBars ? setBars(state.changePercentages) : ""}
          {!state.apiError && (
            <div className="pop-age-year">
              {translate("app.changeInPopulation.ageCohort.populationAgeYears")}
            </div>
          )}
        </div>
      )}

      {state.apiError && <ApiError />}
    </>
  );
}

const displayAgeGroup = (population: PopulationByAge) => {
  if (population.ageFrom === "0")
    return translate("app.changeInPopulation.ageCohort.lowestAgeGroup");
  if (population.ageFrom === "85")
    return translate("app.changeInPopulation.ageCohort.highestAgeGroup");

  return translate("app.changeInPopulation.ageCohort.ageGroup", {
    ageFrom: population.ageFrom,
    ageTo: population.ageTo
  });
};

const getPopulationAgeRange = (population: PopulationByAge) => {
  if (population.ageFrom === "0") return "> 5";

  if (population.ageFrom === "85") return "85 <";

  return population.ageFrom + " to " + population.ageTo;
};

const addClassListToElement = (item: Element, classesToAdd: string) => {
  item.classList.add(classesToAdd);
};

const getElementListAndSetClassList = (
  elementTofetch: string,
  classToAdd: string
) => {
  var element = document.querySelectorAll(elementTofetch);
  element.forEach(setClassList);
  function setClassList(item: Element, index: number) {
    addClassListToElement(item, classToAdd);
  }
};

const getNegativeValBarView = () => {
  getElementListAndSetClassList(".col", "col-height");

  var barElement = document.querySelectorAll(".barss");
  barElement.forEach(removeBarClassList);
  function removeBarClassList(item: Element, index: number) {
    item.classList.remove("negative");
  }

  getElementListAndSetClassList(".label", "-label-tranform");

  getElementListAndSetClassList(".x-axis", "-transform");

  getElementListAndSetClassList(".segment", "-border-top");

  getElementListAndSetClassList(".tooltip-text", "-transform-tooltip");

  getElementListAndSetClassList(".cohort-chart", "-negative-space");
};

const getPostiveValBarView = () => {
  getElementListAndSetClassList(".col", "col-height");
  var barElement = document.querySelectorAll(".barss");
  barElement.forEach(removeBarClassList);
  function removeBarClassList(item: Element) {
    item.classList.remove("negative");
  }
};

const setBarClassList = (valStatus: string) => {
  switch (valStatus) {
    case "negative":
      return getNegativeValBarView();

    default:
      return getPostiveValBarView();
  }
};

const absoluteValueArray = (percentages: number[]) => {
  return percentages.map(Math.abs);
};

const getInterval = (largest: number, ticks: number) => {
  const minimum = largest / ticks;
  const magnitude = 10 ** Math.floor(Math.log10(minimum));
  const residual = minimum / magnitude;
  let interval;
  if (residual > 5) {
    interval = 10 * magnitude;
  } else if (residual > 3) {
    interval = 5 * magnitude;
  } else if (residual > 2) {
    interval = 3 * magnitude;
  } else if (residual > 1.5) {
    interval = 2 * magnitude;
  } else if (residual > 1) {
    interval = 1.5 * magnitude;
  } else {
    interval = magnitude;
  }
  return interval;
};
